import { useEffect, useMemo, useState } from 'react';
import { debounce } from 'lodash';
import styled from 'styled-components';

import { useQuery } from '@tanstack/react-query';
import { SortingState } from '@tanstack/react-table';

import Page from 'components/layouts/page/Page';
import AccessGuard from 'components/shared/AccessGuard';
import Button from 'components/shared/Button';
import Search from 'components/shared/Search';
import ControlledTable from 'components/shared/table/ControlledTable';
import usePagination from 'hooks/usePagination';
import { PERMISSIONS } from 'models/Profile';
import { ReferralSource } from 'models/ReferralSource';
import { indexReferralSources, referralSourceQueryKeys } from 'services/api/referralSources';
import { serializeSort } from 'stores/helpers';
import { BodySmall, H2 } from 'styles/typography';
import PlusIcon from 'svg/PlusIcon';

import DeleteReferralSourceModal from './DeleteReferralSourceModal';
import referralSourceColumns from './referralSourceColumns';
import ReferralSourceEmptyState from './ReferralSourceEmptyState';
import ReferralSourceModal from './ReferralSourceModal';

// Matches the API default (Date Added, newest first) so the first load mirrors the mock.
const DEFAULT_SORT: SortingState = [{ id: 'dateAdded', desc: true }];

type ActiveModal =
  | { type: 'add' }
  | { type: 'edit'; referralSource: ReferralSource }
  | { type: 'delete'; referralSource: ReferralSource };

export type ReferralSourceListProps = {
  // Absent means the acting client's own list (the Configuration page). Present means an Olio admin
  // is looking at that client's list from Admin > Clients — the same screen, pointed elsewhere,
  // which is how notification preferences already work.
  clientId?: string;
  isAdmin?: boolean;
  title?: string;
};

export default function ReferralSourceList({ clientId, isAdmin = false, title }: ReferralSourceListProps) {
  const pagination = usePagination();
  const [sort, setSort] = useState<SortingState>(DEFAULT_SORT);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null);

  // Debounced so a keystroke does not fire a request each time. Unlike the Denial List, the sort is
  // left alone while searching: there is no relevance sort on this endpoint, and for one client's
  // referral sources a predictable order beats a ranked one.
  const applySearch = useMemo(() => debounce((value: string) => setSearch(value), 500), []);

  useEffect(() => () => applySearch.cancel(), [applySearch]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    applySearch(value);
  };

  const queryParams = useMemo(
    () => ({
      clientId,
      page: pagination.paging.page,
      pageSize: pagination.paging.pageSize,
      sortBy: serializeSort(sort),
      search,
    }),
    [clientId, pagination.paging, sort, search]
  );

  useEffect(() => {
    pagination.setPageIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: referralSourceQueryKeys.index(queryParams),
    queryFn: ({ signal }) => indexReferralSources(queryParams, signal),
  });

  const columns = useMemo(
    () =>
      referralSourceColumns({
        isAdmin,
        onEdit: (referralSource) => setActiveModal({ type: 'edit', referralSource }),
        onDelete: (referralSource) => setActiveModal({ type: 'delete', referralSource }),
      }),
    [isAdmin]
  );

  const closeModal = () => setActiveModal(null);

  const totalRecords = data?.meta.totalRecords ?? 0;
  const showEmptyState = !isLoading && totalRecords === 0 && !search;
  const createPermission = isAdmin ? PERMISSIONS.adminReferralSourceCreate : PERMISSIONS.referralSourceCreate;

  return (
    <Page>
      <Page.Header>
        <HeaderRow>
          <TitleGroup>
            <Title>{title ?? 'Referral Sources'}</Title>
            <SearchContainer>
              <Search value={searchInput} onChange={handleSearchChange} placeholder='Search' />
            </SearchContainer>
          </TitleGroup>
          <AccessGuard accessor={createPermission}>
            <Button variant='primary' onClick={() => setActiveModal({ type: 'add' })} data-testid='add-referral-source'>
              <PlusIcon width={16} height={16} />
              Add New Referral Source
            </Button>
          </AccessGuard>
        </HeaderRow>
      </Page.Header>

      <Page.SubHeader>
        <BodySmall>
          {totalRecords} {totalRecords === 1 ? 'Referral Source' : 'Referral Sources'} found
        </BodySmall>
      </Page.SubHeader>

      <Page.Content>
        {showEmptyState ? (
          <ReferralSourceEmptyState />
        ) : (
          <ControlledTable<ReferralSource>
            data={data?.data ?? []}
            columns={columns}
            loading={isLoading}
            pageCount={data?.meta.totalPages ?? 1}
            pagination={pagination}
            sorting={{ sortBy: sort, setSortBy: setSort }}
            // The API applies the sort and has no fallback ORDER BY beyond the one it picks, so an
            // unsorted list would paginate non-deterministically. Keep every header click on a
            // direction rather than cycling back to no sort.
            enableSortingRemoval={false}
          />
        )}
      </Page.Content>

      {(activeModal?.type === 'add' || activeModal?.type === 'edit') && (
        <ReferralSourceModal
          referralSource={activeModal.type === 'edit' ? activeModal.referralSource : undefined}
          clientId={clientId}
          onClose={closeModal}
        />
      )}

      {activeModal?.type === 'delete' && (
        <DeleteReferralSourceModal
          referralSource={activeModal.referralSource}
          clientId={clientId}
          onClose={closeModal}
        />
      )}
    </Page>
  );
}

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchContainer = styled.div`
  width: 280px;
`;

const Title = styled(H2)`
  font-size: 20px;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
`;
